import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import ContentWrapper from '../ContentWrapper';
import FormCard from '../FormCard';
import PersonalIdentification from './components/PersonalIdentification';
import PracticeLicense from './components/PracticeLicense';
import MalpracticeInsuranceCertificate from './components/MalpracticeInsuranceCertificate';
import DEALicense from './components/DeaLicense';
import CollaboratingPhysician from './components/CollaboratingPhysician';
import AdditionalLicense from './components/AdditionalLicense';
import { useLicenseInfo } from 'hooks/newUniprofile/useLicenseInfo';
import { Spin, message } from 'antd';
import { editIdentification } from 'api/newUniprofile';
import { formatDate } from 'utils/common';

interface LicenseStepProps {
    isReview?: boolean;
}

const LicenseStep = forwardRef(({ isReview = false }: LicenseStepProps, ref) => {
    const [licenseInfo, loading, fetchData] = useLicenseInfo();
    const personalIdentificationRef = useRef<any>();
    const practiceLicenseRef = useRef<any>();
    const malpracticeInsuranceCertificateRef = useRef<any>();
    const deaLicenseRef = useRef<any>();
    const collaboratingPhysicianRef = useRef<any>();
    const additionalLicenseRef = useRef<any>();
    console.log('licenseInfo', licenseInfo);

    const onSubmit = async () => {
        try {
            const isEditingRefs = [personalIdentificationRef.current, practiceLicenseRef.current];
            if (malpracticeInsuranceCertificateRef.current.isEditing) {
                isEditingRefs.push(malpracticeInsuranceCertificateRef.current);
            }
            if (deaLicenseRef.current.isEditing) {
                isEditingRefs.push(deaLicenseRef.current);
            }
            if (collaboratingPhysicianRef.current.isEditing) {
                isEditingRefs.push(collaboratingPhysicianRef.current);
            }
            if (additionalLicenseRef.current.isEditing) {
                isEditingRefs.push(additionalLicenseRef.current);
            }
            const validateResult = await Promise.all([
                ...isEditingRefs.map((item) => item.onValidate()),
            ]);

            const res = await editIdentification({
                identification: validateResult[0],
                licenses: validateResult[1].licenses.map((item: any) => ({
                    ...item,
                    expireDate: formatDate(item.expireDate),
                    licenseType: '',
                })),
            });
            if (res.error) {
                message.error(res.error);
            } else {
                await fetchData();
            }
        } catch (error) {
            console.error(error);
            message.error('Please check the form and try again.');
            throw error;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
    }));

    const hasAdditionalLicense = !!licenseInfo?.additionalLicensesNCertificates?.controlledSubstanceCertification?.length ||
        !!licenseInfo?.additionalLicensesNCertificates?.continuingMedicalEducationCertificates?.length ||
        !!licenseInfo?.additionalLicensesNCertificates?.autonomy?.url ||
        !!licenseInfo?.additionalLicensesNCertificates?.other?.url;

    return (
        <Spin spinning={loading}>
            <ContentWrapper title="License" description="Verify your identity and license to complete your setup." showInfo={!isReview}>
                <FormCard type={isReview ? 'review' : 'edit'} title="Personal identification" showHeader={isReview} ref={personalIdentificationRef}>
                    {(isEditing) => <PersonalIdentification isEditing={isEditing} isReview={isReview} initValue={licenseInfo?.identification} refetch={fetchData} />}
                </FormCard>
                <FormCard type={isReview ? 'review' : 'edit'} title="Practice licenses" ref={practiceLicenseRef} showHeader>
                    {(isEditing) => <PracticeLicense isEditing={isEditing} initValue={licenseInfo?.practiceLicense} refetch={fetchData} />}
                </FormCard>
                <FormCard type={isReview ? 'review' : 'add'} title="Malpractice insurance certificate" isOptional ref={malpracticeInsuranceCertificateRef} hasContent={!!licenseInfo?.malpracticeInsuranceCertificate?.licenseNumber}>
                    {(isEditing) => <MalpracticeInsuranceCertificate isEditing={isEditing} initValue={licenseInfo?.malpracticeInsuranceCertificate} refetch={fetchData} />}
                </FormCard>
                <FormCard type={isReview ? 'review' : 'add'} title="DEA licenses" isOptional ref={deaLicenseRef} hasContent={!!licenseInfo?.deaLicense?.length}>
                    {(isEditing) => <DEALicense isEditing={isEditing} initValue={licenseInfo?.deaLicense} refetch={fetchData} />}
                </FormCard>
                <FormCard type={isReview ? 'review' : 'add'} title="Collaborating physician" isOptional ref={collaboratingPhysicianRef} hasContent={!!licenseInfo?.collaboratingPhysician?.length}>
                    {(isEditing) => <CollaboratingPhysician isEditing={isEditing} initValue={licenseInfo?.collaboratingPhysician} refresh={fetchData} />}
                </FormCard>
                <FormCard type={isReview ? 'review' : 'add'} title="Additional licences and certificates" isOptional ref={additionalLicenseRef} hasContent={hasAdditionalLicense}>
                    {(isEditing) => (
                        <AdditionalLicense
                            isEditing={isEditing}
                            initValue={licenseInfo?.additionalLicensesNCertificates}
                            refetch={fetchData}
                        />
                    )}
                </FormCard>
            </ContentWrapper>
        </Spin>
    );
});

export default LicenseStep;
